import {
  AuthActions,
  AuthSelectors,
  ExperimentsActions,
  FirebaseActions,
  FirebaseActionTypes,
  LanguageActions,
  ObFlowService,
  StatsActions,
} from '@web_opxp/features';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';
import { ApiService } from 'web_core_library';
import { detectLandingPageTracking } from '../base/cookieService';
import ENV from '../base/env';
import * as HistoryService from '../base/historyService';
import { CheckoutActions } from '../checkout';
import { CouponActions } from '../coupon';
import { SubscribeActions } from '../subscribe';
import { AppActions } from './slice';

export function* handleAppStart() {
  // init i18n feature
  yield put(LanguageActions.initLanguage());
  // init firebase
  const searchParams: URLSearchParams = yield call(HistoryService.getCurrentSearchParams);
  yield call(ObFlowService.init, 'subscribe', searchParams, ENV.AssetsBaseUrl, {
    getExternalUrl: HistoryService.getExternalUrl,
    getInternalUrl: HistoryService.getInternalUrl,
    getRegistrationUrl: HistoryService.getRegistrationUrl,
    redirectExternal: HistoryService.externalRedirect,
    redirectInternal: HistoryService.redirectInternal,
    redirectRegistration: HistoryService.redirectSignup,
    urlMap: {
      training: ENV.TrainingAppPath,
      sso: ENV.AuthBaseUrl,
      onboarding: ENV.OnboardingAppPath,
      user: ENV.UserAppPath,
    },
  });
  yield put(
    FirebaseActions.firebaseInitAction(
      ENV.FirebaseConfig.apiKey,
      ENV.FirebaseConfig.projectId,
      ENV.FirebaseConfig.appId,
      ENV.FirebaseConfig.messagingSenderId,
      ENV.FirebaseConfig.measurementId,
      searchParams
    )
  );
  yield take(FirebaseActionTypes.FIREBASE_INITIALIZED);
  yield call(detectLandingPageTracking);

  yield put(FirebaseActions.firebaseStartAction({ analytics: true, config: true, messaging: false }));

  // Init ApiService
  yield call(ApiService.init, ENV.ApiUrl);

  // start session token restoration
  yield put(
    AuthActions.authRestore({
      getCurrentSearchParams: HistoryService.getCurrentSearchParams,
      exchangeAccessToken: HistoryService.exchangeAccessToken,
      updateSearchParams: HistoryService.updateSearchParams,
      refreshToken: HistoryService.refreshToken,
      redirectLogin: HistoryService.redirectLogin,
      redirectLogout: HistoryService.redirectLogout,
    })
  );
  const authResult = yield take([AuthActions.restoreAuthSuccess, AuthActions.restoreAuthFail]);
  if (authResult.type === AuthActions.restoreAuthSuccess.type) {
    const userId = yield select(AuthSelectors.getUserId);
    // set user id for tracking
    yield put(FirebaseActions.setUserAction(userId));
    // load experiments feature as soon as possible
    yield call(loadExperimentsFeature);
    // initialize stats feature
    yield put(StatsActions.initStats(userId));
    yield take(StatsActions.statsReady);
    // initialize checkout feature
    yield put(CheckoutActions.initCheckout());
  }
  // initialize coupon feature
  yield put(CouponActions.couponInit());
  // initialize subscribe feature
  yield put(SubscribeActions.subscribeInit());
  // set app to started state
  yield put(AppActions.appStarted());
}

export function* loadExperimentsFeature() {
  const experiments = [];
  yield put(ExperimentsActions.initExperiments({ experiments }));
  yield take(ExperimentsActions.experimentsReady);
}

export default function* watchApp() {
  yield all([takeEvery(AppActions.appStart, handleAppStart)]);
}
